import { MutableRefObject, useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebouncedCallback } from 'use-debounce';
import { v4 as uuidv4 } from 'uuid';
import { toast } from 'react-toastify';
import { UseDataProps } from './types';
import { AttachmentItemData, DrawDesc } from '90.quickConnect.Models/models';
import { AllFieldValueTypes, FieldDesc } from '90.quickConnect.Models/models';
import attachmentDb from '40.quickConnect.DataAccess/indexedDb/dbs/attachmentDb';

import { blackColor } from '00.app/theme';
import CustomLogger from '80.quickConnect.Core/logger/customLogger';
import { useStore } from '30.quickConnect.Stores';
import { createBase64FromCanvas } from '50.quickConnect.Fields/FieldsTypes/Attachments/helpers/bipMapDrawer';

// Helpers généraux

/** Vérifie si un MIME est vraiment supporté pour l'encodage canvas (cache le résultat). */
const mimeSupportCache: Record<string, boolean> = {};
const isMimeSupported = (mime: string): boolean => {
  if (mimeSupportCache[mime] !== undefined) return mimeSupportCache[mime];
  try {
    const c = document.createElement('canvas');
    c.width = c.height = 1;
    const data = c.toDataURL(mime);
    mimeSupportCache[mime] = data.startsWith(`data:${mime}`);
    return mimeSupportCache[mime];
  } catch {
    mimeSupportCache[mime] = false;
    return false;
  }
};

/** Choisit le meilleur mime dispo parmi une liste. */
const pickBestMime = (candidates: string[], fallback = 'image/png'): string => {
  for (const m of candidates) if (isMimeSupported(m)) return m;
  return fallback;
};

/** Construit un setter qui révoque automatiquement l'ancienne blob: URL */
const makeBgSetter = (setBackGroundImage: (s: string) => void) => {
  let lastUrl: string | null = null;
  return (url: string) => {
    setBackGroundImage(url);
    const old = lastUrl;
    lastUrl = url;
    if (old && old.startsWith('blob:') && old !== url) {
      requestAnimationFrame(() => URL.revokeObjectURL(old));
    }
  };
};

/** Vignette base64 issue du modèle de données */
const toThumbDataUrl = (img?: AttachmentItemData) => {
  if (!img?.thumbnail) return '';
  const mime = img.type || 'image/png';
  return `data:${mime};base64,${img.thumbnail}`;
};

/** Taille du conteneur du canvas */
const getBoxSize = (canvasRef: MutableRefObject<any>) => {
  const box = canvasRef.current?.canvasContainer as HTMLElement | undefined;
  return { w: box?.clientWidth || 800, h: box?.clientHeight || 600 };
};

/* === Offscreen canvas réutilisé (perf/mémoire) === */
const useOffscreen = () => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const ctxRef = useRef<CanvasRenderingContext2D | null>(null);

  const ensure = useCallback((w: number, h: number) => {
    if (!canvasRef.current) canvasRef.current = document.createElement('canvas');
    const c = canvasRef.current!;
    if (c.width !== w) c.width = w;
    if (c.height !== h) c.height = h;
    if (!ctxRef.current) ctxRef.current = c.getContext('2d');
    return { c, ctx: ctxRef.current };
  }, []);

  const drawFitToBlob = useCallback(
    async (img: CanvasImageSource, boxW: number, boxH: number, mime: string, quality = 0.9): Promise<Blob> => {
      const { c, ctx } = ensure(boxW, boxH);
      if (!ctx) return new Blob();
      const w = (img as any).naturalWidth ?? (img as any).width;
      const h = (img as any).naturalHeight ?? (img as any).height;

      const scale = Math.min(boxW / w, boxH / h);
      const targetW = Math.max(1, Math.round(w * scale));
      const targetH = Math.max(1, Math.round(h * scale));
      const dx = Math.floor((boxW - targetW) / 2);
      const dy = Math.floor((boxH - targetH) / 2);

      ctx.clearRect(0, 0, boxW, boxH);
      ctx.drawImage(img, dx, dy, targetW, targetH);

      const blob: Blob = await new Promise((res) => c.toBlob((b) => res(b || new Blob()), mime, quality));
      return blob;
    },
    [ensure],
  );

  return { drawFitToBlob };
};

/** Fit d'un fichier/Blob -> blob: URL (utilise createImageBitmap si dispo) */
const fitBlobToBlobUrl = async (
  file: Blob,
  boxW: number,
  boxH: number,
  drawFitToBlob: (img: CanvasImageSource, w: number, h: number, mime: string, q?: number) => Promise<Blob>,
  preferred: string[] = ['image/webp', 'image/jpeg', 'image/png'],
) => {
  const mime = pickBestMime(preferred);
  if ('createImageBitmap' in window) {
    const bitmap = await createImageBitmap(file);
    const blob = await drawFitToBlob(bitmap, boxW, boxH, mime, 0.9);
    return URL.createObjectURL(blob);
  }
  // fallback sans createImageBitmap
  const url = URL.createObjectURL(file);
  const img = new Image();
  const out = await new Promise<string>((resolve) => {
    img.onload = async () => {
      const blob = await drawFitToBlob(img, boxW, boxH, mime, 0.9);
      resolve(URL.createObjectURL(blob));
    };
    img.onerror = () => resolve(url); // au pire on garde l'URL brute (sera révoquée à la prochaine)
  });
  requestAnimationFrame(() => URL.revokeObjectURL(url));
  return out;
};

/** Fit d'un dataURL (thumbnail) -> blob: URL */
const fitDataUrlToBlobUrl = async (
  dataUrl: string,
  boxW: number,
  boxH: number,
  drawFitToBlob: (img: CanvasImageSource, w: number, h: number, mime: string, q?: number) => Promise<Blob>,
  preferred: string[] = ['image/webp', 'image/jpeg', 'image/png'],
) => {
  const mime = pickBestMime(preferred);
  const img = new Image();
  const p = new Promise<string>((resolve) => {
    img.onload = async () => {
      const blob = await drawFitToBlob(img, boxW, boxH, mime, 0.9);
      resolve(URL.createObjectURL(blob));
    };
    img.onerror = () => resolve(dataUrl); // fallback
  });
  img.src = dataUrl;
  return p;
};

const useData = (
  updateDeclaration: (updatedFieldFullPathId: string, newValue: AllFieldValueTypes) => void,
  field: FieldDesc,
  drawDesc: DrawDesc,
  setSelectedIndex: React.Dispatch<React.SetStateAction<string | undefined>>,
): UseDataProps => {
  // Tag
  const tag = '50.quickConnect.Fields/FieldsTypes/Attachments/DrawQcField/hooks.tsx';

  const logger = CustomLogger.getInstance();
  const { fullPathId, value, fieldType, maxAttachment, maximumSize, defaultImage } = drawDesc;
  // On récupère la translation
  const { t } = useTranslation('declaration');
  const [brushRadius, setBrushRadius] = useState(10);
  const [brushColor, setBrushColor] = useState(blackColor);
  const [signatureList, setSignatureList] = useState<string[]>([]);
  const [signatureFileList, setSignatureFileList] = useState<AttachmentItemData[]>(
    (value as AttachmentItemData[]) ?? [],
  );

  // BackGround image
  const [backGroundImage, setBackGroundImage] = useState<string>('');
  const setBg = useMemo(() => makeBgSetter(setBackGroundImage), [setBackGroundImage]); // revoke auto
  useEffect(() => () => setBg(''), [setBg]); // cleanup

  const [isChecked, setIsChecked] = useState<boolean>(false);
  const canvasRef: MutableRefObject<any> = useRef();

  const offscreen = useOffscreen();

  const bgOpRef = useRef(0);

  // cache objectURL des pièces (évite leaks/reload)
  const urlCacheRef = useRef<Map<string, string>>(new Map());
  const revokeAllCachedUrls = useCallback(() => {
    for (const url of urlCacheRef.current.values()) {
      if (url.startsWith('blob:')) URL.revokeObjectURL(url);
    }
    urlCacheRef.current.clear();
  }, []);

  const updateGlobalState = useCallback(() => {
    updateDeclaration(fullPathId, signatureFileList);
  }, [fullPathId, signatureFileList, updateDeclaration]);

  const {
    DeclarationStore: { declarationId },
  } = useStore();

  const debouncedUpdateGlobalState = useDebouncedCallback(() => {
    updateGlobalState();
  });

  /** Revenir à la photo par défaut (si dispo) */
  const resetToDefaultBackground = useCallback(async () => {
    if (!defaultImage) {
      setBg('');
      return;
    }
    const { w, h } = getBoxSize(canvasRef);
    const fallback = toThumbDataUrl(defaultImage);
    if (fallback) {
      const myVersion = ++bgOpRef.current;
      const fitted = await fitDataUrlToBlobUrl(fallback, w, h, offscreen.drawFitToBlob);
      if (bgOpRef.current === myVersion) setBg(fitted);
      return;
    }
    setBg('');
  }, [defaultImage, setBg, offscreen.drawFitToBlob]);

  /** Image par défaut au montage / changement de defaultImage */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (cancelled) return;
      await resetToDefaultBackground();
    })();
    return () => {
      cancelled = true;
    };
  }, [resetToDefaultBackground]);

  const dataURItoBlob = (dataURI: string, fileName: string) => {
    const byteString = atob(dataURI.split(',')[1]);
    // eslint-disable-next-line prefer-destructuring
    const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);

    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }

    return new File([ab], fileName, { type: mimeString });
  };

  const handleExport = useCallback(async () => {
    const b64 =
      backGroundImage !== ''
        ? await createBase64FromCanvas(canvasRef.current.canvasContainer)
        : canvasRef.current?.getDataURL('png', false, '#FFFFFF');

    logger.log(tag, `'fieldType: ', ${fieldType}`);

    const fileName = `QC_${b64.split(';')[0].split('/')[1].toUpperCase()}_${uuidv4()}`;
    // Pour eviter l'ajout d'une signature vide
    setSignatureList([...signatureList, b64]);
    const blobFile = dataURItoBlob(b64, fileName);

    const obj = {
      id: uuidv4(),
      localUri: fileName,
      distantUri: '',
      title: fileName,
      fileName: fileName,
      creationDate: new Date(),
      purgeDate: '',
      type: blobFile.type,
      size: blobFile.size ?? 0,
      metadata: [],
      thumbnail: b64.replace(/^data:image\/[a-z]+;base64,/, ''),
      icon: '',
      file: blobFile,
    } as AttachmentItemData;
    if (maxAttachment > signatureFileList.length) {
      if (maximumSize * 1024 ** 2 >= blobFile.size) {
        setSignatureFileList((prev) => [...prev, obj]);
        canvasRef.current?.clear();
        await attachmentDb.addAttachmentItem(obj, blobFile, declarationId);
        debouncedUpdateGlobalState();
        setSelectedIndex(undefined);
      } else {
        toast.warn(`${t('attachment_fields_errors_has_max_size')} (${maximumSize} Mo)`);
      }
    } else {
      toast.warn(`${t('attachment_fields_errors_has_max_count')} (${maxAttachment})`);
    }
  }, [
    backGroundImage,
    logger,
    fieldType,
    signatureList,
    maxAttachment,
    signatureFileList.length,
    maximumSize,
    declarationId,
    debouncedUpdateGlobalState,
    setSelectedIndex,
    t,
  ]);

  const onDeleteSignature = useCallback(
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, data: AttachmentItemData) => {
      event.preventDefault();
      event.stopPropagation();
      const filtredList = signatureFileList.filter((x) => x.id !== data.id);
      setSignatureFileList(filtredList);
      debouncedUpdateGlobalState();
    },
    [debouncedUpdateGlobalState, signatureFileList],
  );
  const handleClear = useCallback(() => {
    canvasRef.current?.clear();
    void resetToDefaultBackground();
  }, [resetToDefaultBackground]);

  const handleErase = useCallback(() => {
    setIsChecked((prev) => !prev);

    if (!isChecked) canvasRef.current.ctx.drawing.globalCompositeOperation = 'destination-out';
    else canvasRef.current.ctx.drawing.globalCompositeOperation = 'source-over';
  }, [isChecked]);

  const attachmentsItemsData: AttachmentItemData[] = useMemo(() => {
    if (!Array.isArray(value)) return [];

    return value;
  }, [value]);

  useEffect(() => {
    if (
      signatureFileList.some(
        (attachmentItemData: AttachmentItemData) =>
          attachmentItemData.file &&
          (attachmentItemData.file instanceof File === false || attachmentItemData.file instanceof Blob === false),
      )
    ) {
      Promise.all(
        signatureFileList.map(async (attach: AttachmentItemData) => {
          const localFile = await attachmentDb.getAttachmentById(attach.id);
          attach.file = localFile?.file ?? undefined;
          return attach;
        }),
      ).then((newSignatureFileList) => {
        setSignatureFileList((prevState: AttachmentItemData[]): AttachmentItemData[] =>
          prevState !== newSignatureFileList ? newSignatureFileList : prevState,
        );
      });
    }
  }, [field.fieldType, signatureFileList]);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      event.preventDefault();
      const { files } = event.currentTarget;
      if (files && files[0]) {
        const { w, h } = getBoxSize(canvasRef);
        const version = ++bgOpRef.current;
        void (async () => {
          const fittedBlobUrl = await fitBlobToBlobUrl(files[0], w, h, offscreen.drawFitToBlob);
          if (bgOpRef.current === version) setBg(fittedBlobUrl); // anti-race
        })();
      }
    },
    [setBg, offscreen.drawFitToBlob],
  );

  // src
  const getSrcValue = useCallback((attachItem: AttachmentItemData) => {
    return attachItem.distantUri
      ? attachItem.distantUri
      : attachItem.file &&
        (attachItem.file instanceof File || attachItem.file instanceof Blob) &&
        Object.keys(Object.getPrototypeOf(attachItem.file)).length > 0
      ? URL.createObjectURL(attachItem.file as Blob)
      : '';
  }, []);

  /** Cleanup cache quand la liste change fortement ou au démontage */
  useEffect(() => {
    return () => {
      revokeAllCachedUrls();
    };
  }, [revokeAllCachedUrls]);

  /** Sync le state local avec la valeur externe du champ */
  useEffect(() => {
    if (Array.isArray(value)) setSignatureFileList(value);
    else if (value == null || value === '') setSignatureFileList([]);
  }, [value]);

  const handleUndo = useCallback((event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.preventDefault();
    canvasRef.current.undo();
  }, []);

  // charger la DERNIERE pièce comme fond (ou défaut s’il n’y a rien)
  const setBackgroundFromAttachment = useCallback(
    async (attach?: AttachmentItemData) => {
      const { w, h } = getBoxSize(canvasRef);
      if (!attach) {
        await resetToDefaultBackground();
        return;
      }

      if (attach.file && (attach.file instanceof File || attach.file instanceof Blob)) {
        const version = ++bgOpRef.current;
        const fitted = await fitBlobToBlobUrl(attach.file as Blob, w, h, offscreen.drawFitToBlob);
        if (bgOpRef.current === version) setBg(fitted);
        return;
      }

      const thumb = toThumbDataUrl(attach);
      if (thumb) {
        const version = ++bgOpRef.current;
        const fitted = await fitDataUrlToBlobUrl(thumb, w, h, offscreen.drawFitToBlob);
        if (bgOpRef.current === version) setBg(fitted);
        return;
      }

      await resetToDefaultBackground();
    },
    [canvasRef, setBg, resetToDefaultBackground, offscreen.drawFitToBlob],
  );

  const loadLastEditedAttachmentAsBackground = useCallback(async () => {
    const items = signatureFileList && signatureFileList.length > 0 ? signatureFileList : attachmentsItemsData;
    if (!items || items.length === 0) {
      await resetToDefaultBackground();
      return;
    }

    const sorted = items.slice().sort((a, b) => {
      const da = new Date(a.creationDate || 0).getTime();
      const db = new Date(b.creationDate || 0).getTime();
      return da - db;
    });

    const last = sorted[sorted.length - 1];
    await setBackgroundFromAttachment(last);
  }, [signatureFileList, attachmentsItemsData, setBackgroundFromAttachment, resetToDefaultBackground]);

  return {
    handleUndo,
    t,
    brushRadius,
    setBrushRadius,
    brushColor,
    setBrushColor,
    signatureList,
    canvasRef,
    handleExport,
    handleClear,
    signatureFileList,
    onDeleteSignature,
    backGroundImage,
    handleChange,
    handleErase,
    isChecked,
    attachmentsItemsData,
    getSrcValue,
    loadLastEditedAttachmentAsBackground,
  };
};

export default useData;
